// A4 PDF generators for KELTA — Digital ID Card & Payment Receipt.
// Uses jsPDF vector graphics on top of the KELTA branded template.
import { jsPDF } from "jspdf";
import QRCode from "qrcode";

// ---- KELTA Brand palette (from official logo) ----
// Primary blue #005EB8 · Saffron #FF9933 · Green #138808
const KELTA_BLUE = [0, 94, 184];
const KELTA_BLUE_DEEP = [0, 66, 130];
const KELTA_BLUE_SOFT = [45, 115, 195];
const KELTA_BLUE_TINT = [232, 242, 253];
const SAFFRON = [255, 153, 51];
const SAFFRON_SOFT = [255, 214, 170];
const KELTA_GREEN = [19, 136, 8];
const KELTA_GREEN_SOFT = [200, 232, 187];
const INK = [30, 30, 35];
const MUTED = [110, 118, 132];
const LINE = [220, 224, 232];

// ---- Helpers ----
const A4 = { w: 210, h: 297 };

async function loadDataUrl(src) {
  if (!src) return null;
  try {
    const res = await fetch(src, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function genQR(text) {
  try {
    return await QRCode.toDataURL(text, {
      errorCorrectionLevel: "H",
      margin: 0,
      width: 512,
      color: { dark: "#005EB8", light: "#FFFFFF" },
    });
  } catch {
    return null;
  }
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(+dt)) return String(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtMoney(n) {
  const v = Number(n) || 0;
  return "INR " + v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function numToIndianWords(num) {
  const n = Math.round((Number(num) || 0) * 100);
  const rupees = Math.floor(n / 100);
  const paise = n % 100;
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigits = (x) => x < 20 ? ones[x] : tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : "");
  const threeDigits = (x) => {
    const h = Math.floor(x / 100), r = x % 100;
    return (h ? ones[h] + " Hundred" + (r ? " and " : "") : "") + (r ? twoDigits(r) : "");
  };
  const inWords = (x) => {
    if (x === 0) return "Zero";
    const cr = Math.floor(x / 10000000); x %= 10000000;
    const lk = Math.floor(x / 100000); x %= 100000;
    const th = Math.floor(x / 1000); x %= 1000;
    const hn = x;
    let out = "";
    if (cr) out += twoDigits(cr) + " Crore ";
    if (lk) out += twoDigits(lk) + " Lakh ";
    if (th) out += twoDigits(th) + " Thousand ";
    if (hn) out += threeDigits(hn);
    return out.trim();
  };
  let str = inWords(rupees) + " Rupees";
  if (paise) str += " and " + inWords(paise) + " Paise";
  return str + " only";
}

// ---- Cached assets (loaded once per session) ----
let _crest = null, _wordmark = null, _sign = null, _presSign = null, _secSign = null;
async function loadBrandAssets() {
  if (!_crest) _crest = await loadDataUrl("/kelta-crest-v2.jpg");
  if (!_wordmark) _wordmark = await loadDataUrl("/kelta-wordmark-v2.jpg");
  if (!_sign) _sign = await loadDataUrl("/treasurer-sign.png");
  if (!_presSign) _presSign = await loadDataUrl("/president-sign.png");
  if (!_secSign) _secSign = await loadDataUrl("/secretary-sign.png");
  return { crest: _crest, wordmark: _wordmark, sign: _sign, presSign: _presSign, secSign: _secSign };
}

// Draw the branded page background + top logo block. Returns y-cursor after the logo strip.
async function drawBrandBackground(doc, { badgeTitle, badgeSubtitle } = {}) {
  const { crest, wordmark } = await loadBrandAssets();
  // White page background with a KELTA-blue band at the very bottom (drawn later per-doc).
  // We intentionally skip the full-bleed template image so the page reads as white.
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, A4.w, A4.h, "F");
  // Top-left crest
  if (crest) {
    try { doc.addImage(crest, "JPEG", 14, 12, 22, 22); } catch { /* ignore */ }
  }
  // Wordmark aligned next to crest
  if (wordmark) {
    // Wordmark aspect ~ 2000:680 ≈ 2.94 : 1
    const wmW = 62, wmH = wmW / 2.94;
    try { doc.addImage(wordmark, "JPEG", 40, 15, wmW, wmH); } catch { /* ignore */ }
  }
  // Tagline under the wordmark
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text("A State-Level Professional Body  •  Est. 2014", 40, 34);

  // Top-right blue badge
  if (badgeTitle) {
    const bx = A4.w - 70, by = 12, bw = 56, bh = 22;
    doc.setFillColor(...KELTA_BLUE);
    doc.roundedRect(bx, by, bw, bh, 3, 3, "F");
    // small doc icon square
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(bx + 4, by + 4, 14, 14, 1.5, 1.5, "F");
    doc.setDrawColor(...KELTA_BLUE);
    doc.setLineWidth(0.3);
    doc.line(bx + 7, by + 8, bx + 15, by + 8);
    doc.line(bx + 7, by + 11, bx + 15, by + 11);
    doc.line(bx + 7, by + 14, bx + 13, by + 14);
    // title text
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    const lines = badgeTitle.split("\n");
    lines.forEach((l, i) => doc.text(l, bx + 22, by + 10 + i * 5));
    if (badgeSubtitle) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.text(badgeSubtitle, bx + 22, by + 18);
    }
  }
  return 46;
}

// Small tricolor accent bar
function drawTricolorAccent(doc, x, y, w = 40, h = 1.6) {
  const seg = w / 3;
  doc.setFillColor(...KELTA_BLUE);
  doc.rect(x, y, seg, h, "F");
  doc.setFillColor(...SAFFRON);
  doc.rect(x + seg, y, seg, h, "F");
  doc.setFillColor(...KELTA_GREEN);
  doc.rect(x + 2 * seg, y, seg, h, "F");
}

// Circular icon badge (used on meta cells + footer notes)
function drawIconCircle(doc, cx, cy, r = 5, colorRgb = KELTA_BLUE) {
  doc.setFillColor(...colorRgb);
  doc.circle(cx, cy, r, "F");
  // subtle inner ring
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.4);
  doc.circle(cx, cy, r - 1, "S");
}

// Meta cell (icon + LABEL + VALUE)
function drawMetaCell(doc, x, y, w, iconRgb, label, value) {
  drawIconCircle(doc, x + 8, y + 9, 5, iconRgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text(label, x + 18, y + 7);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...KELTA_BLUE);
  doc.text(String(value || "—"), x + 18, y + 13.5);
}

// ==============================================================
// PUBLIC: Payment Receipt A4 PDF
// ==============================================================
export async function downloadReceiptPDF(payment, member = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  const isOffline = payment.offline || (payment.method && payment.method !== "Razorpay");
  await drawBrandBackground(doc, { badgeTitle: "PAYMENT\nRECEIPT" });

  // Main title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(...KELTA_BLUE);
  doc.text("Official Payment Receipt", 14, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("This is a computer-generated receipt for a payment made to KELTA.", 14, 66);
  drawTricolorAccent(doc, 14, 69, 40, 1.6);

  // OFFLINE tag (top-right, next to title)
  if (isOffline) {
    doc.setFillColor(255, 152, 0); // amber
    doc.roundedRect(A4.w - 62, 55, 48, 8, 1.5, 1.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text("OFFLINE PAYMENT", A4.w - 38, 60.5, { align: "center" });
  }

  // Meta panel — 3 columns
  const mY = 76;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, mY, A4.w - 28, 22, 3, 3, "FD");
  const cellW = (A4.w - 28) / 3;
  drawMetaCell(doc, 14, mY, cellW, KELTA_BLUE, "RECEIPT NO.", payment.receipt_no || "—");
  drawMetaCell(doc, 14 + cellW, mY, cellW, KELTA_BLUE, "TRANSACTION ID", payment.transaction_id || "—");
  drawMetaCell(doc, 14 + 2 * cellW, mY, cellW, KELTA_BLUE, "DATE & TIME",
    payment.date ? new Date(payment.date).toLocaleString("en-IN", {
      day: "numeric", month: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
    }) : "—");

  // Received From
  const rfY = mY + 30;
  drawIconCircle(doc, 20, rfY - 4, 4, KELTA_BLUE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...KELTA_BLUE);
  doc.text("RECEIVED FROM", 27, rfY - 2);
  doc.setDrawColor(...KELTA_BLUE);
  doc.setLineWidth(0.7);
  doc.line(27, rfY, 68, rfY);

  // Received From card
  const rfCardY = rfY + 4;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...LINE);
  doc.roundedRect(14, rfCardY, A4.w - 28, 26, 3, 3, "FD");

  const rows = [
    ["Name", payment.member_name || member.full_name || "—"],
    ["Membership ID", payment.membership_id || member.membership_id || "—"],
    ["District", payment.member_district || member.district || "—"],
    ["Email", payment.member_email || member.email || "—"],
  ];
  const colW = (A4.w - 28) / 2;
  doc.setFontSize(9);
  rows.forEach((r, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const rx = 18 + col * colW;
    const ry = rfCardY + 10 + row * 10;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(r[0], rx, ry);
    doc.text(":", rx + 28, ry);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...KELTA_BLUE);
    const val = String(r[1]);
    const trimmed = val.length > 34 ? val.slice(0, 33) + "…" : val;
    doc.text(trimmed, rx + 32, ry);
  });

  // Particulars table
  const tY = rfCardY + 34;
  // header
  doc.setFillColor(...KELTA_BLUE);
  doc.roundedRect(14, tY, A4.w - 28, 10, 2, 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text("SL.", 20, tY + 6.5);
  doc.text("PARTICULARS", 34, tY + 6.5);
  doc.text("METHOD", 128, tY + 6.5);
  doc.text("AMOUNT (INR)", A4.w - 20, tY + 6.5, { align: "right" });

  // Build particulars line items. For membership payments we split into
  // Membership Fees (₹100) + Working Fund (remainder). For any other
  // purpose (events, etc.) we keep a single line.
  const totalAmt = Number(payment.amount || 0);
  const purposeLc = (payment.purpose || "").toLowerCase();
  const isMembership = purposeLc.includes("membership") || purposeLc === "";
  const items = isMembership && totalAmt >= 100
    ? [
        { label: "Membership Fees", amt: 100 },
        { label: "Working Fund", amt: totalAmt - 100 },
      ]
    : [
        { label: (payment.purpose || "Membership").replace(/^./, c => c.toUpperCase()) + " Payment — KELTA", amt: totalAmt },
      ];

  const rowH = 10;
  const method = (payment.method || "—").toUpperCase();
  let rY = tY + 10;
  items.forEach((r, i) => {
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...LINE);
    doc.rect(14, rY, A4.w - 28, rowH, "FD");
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(String(i + 1).padStart(2, "0"), 20, rY + 6.8);
    doc.text(r.label, 34, rY + 6.8);
    // Show METHOD only on the first row for a cleaner look
    if (i === 0) doc.text(method, 128, rY + 6.8);
    doc.setFont("courier", "bold");
    doc.text(fmtMoney(r.amt).replace(/^₹\s?/, ""), A4.w - 20, rY + 6.8, { align: "right" });
    rY += rowH;
  });

  // total row
  const totalY = rY;
  doc.setFillColor(...KELTA_BLUE_TINT);
  doc.roundedRect(14, totalY, A4.w - 28, 12, 2, 2, "F");
  doc.setDrawColor(...KELTA_BLUE);
  doc.setLineWidth(0.5);
  doc.roundedRect(14, totalY, A4.w - 28, 12, 2, 2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.setTextColor(...KELTA_BLUE);
  doc.text("TOTAL", 34, totalY + 8);
  doc.setFont("courier", "bold");
  doc.text(fmtMoney(totalAmt), A4.w - 20, totalY + 8, { align: "right" });

  // Amount in words + PAID stamp side by side
  const wY = totalY + 22;
  drawIconCircle(doc, 20, wY - 2, 4, KELTA_BLUE);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text("Amount in words:", 27, wY);
  doc.setFont("helvetica", "bolditalic");
  doc.setTextColor(...KELTA_BLUE);
  const words = numToIndianWords(payment.amount);
  const wrapped = doc.splitTextToSize(words, 100);
  doc.text(wrapped, 27, wY + 5);

  // PAID stamp on the right
  const stampX = A4.w - 62, stampY = wY - 6;
  doc.setDrawColor(...KELTA_GREEN);
  doc.setLineWidth(1.2);
  doc.roundedRect(stampX, stampY, 48, 20, 2, 2);
  // circle icon
  doc.setFillColor(...KELTA_GREEN);
  doc.circle(stampX + 8, stampY + 10, 5, "F");
  // Check mark inside
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.8);
  doc.line(stampX + 5.5, stampY + 10, stampX + 7.5, stampY + 12);
  doc.line(stampX + 7.5, stampY + 12, stampX + 10.5, stampY + 8);
  // Text
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...KELTA_GREEN);
  doc.text("PAID", stampX + 30, stampY + 10, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...MUTED);
  doc.text(`Status: ${(payment.status || "success").toUpperCase()}`, stampX + 30, stampY + 15, { align: "center" });

  // Signature block — treasurer's actual signature above the line
  const sigY = wY + 26;
  const { sign } = await loadBrandAssets();
  if (sign) {
    // Sign image aspect ~ 903:678 ≈ 1.33 : 1
    try { doc.addImage(sign, "PNG", 18, sigY - 18, 42, 20); } catch { /* ignore */ }
  }
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(0.4);
  doc.line(18, sigY, 80, sigY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...KELTA_BLUE);
  doc.text("Treasurer, KELTA", 18, sigY + 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text("Authorised Signatory", 18, sigY + 10);

  // Footer notes (3 numbered items with icons)
  const nY = sigY + 20;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  doc.line(14, nY, A4.w - 14, nY);
  const colWidth = (A4.w - 28) / 3;
  const notes = [
    "This is an electronically generated receipt and does not require a physical signature.",
    "Please retain this receipt for your records; it may be required for future reference.",
    "For any queries regarding this payment, write to support@kelta.co.in citing the transaction ID above.",
  ];
  notes.forEach((n, i) => {
    const cx = 14 + i * colWidth;
    drawIconCircle(doc, cx + 5, nY + 8, 4, KELTA_BLUE);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...INK);
    const wrappedN = doc.splitTextToSize(`${i + 1}. ${n}`, colWidth - 12);
    doc.text(wrappedN, cx + 12, nY + 7);
  });

  // Bottom blue band with tagline + generated date
  const bY = A4.h - 22;
  // White cover to hide the template's baked-in KELTA logo above the band
  doc.setFillColor(255, 255, 255);
  doc.rect(0, bY - 18, A4.w, 18, "F");
  doc.setFillColor(...KELTA_BLUE);
  doc.rect(0, bY, A4.w, 22, "F");
  // Small crest at left of band
  const { crest } = await loadBrandAssets();
  if (crest) {
    try { doc.addImage(crest, "JPEG", 14, bY + 4, 14, 14); } catch { /* ignore */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("KELTA", 32, bY + 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.text("Kerala Local Body Technical", 32, bY + 14.5);
  doc.text("Assistants Association", 32, bY + 18);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("CONNECTING PEOPLE   |   EMPOWERING GOVERNANCE", A4.w / 2, bY + 11, { align: "center" });
  doc.text("ENABLING DIGITAL KERALA", A4.w / 2, bY + 15.5, { align: "center" });

  // date on right
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(`Generated ${fmtDate(new Date())}`, A4.w - 14, bY + 13, { align: "right" });

  const fileName = `KELTA_Receipt_${(payment.receipt_no || "receipt").replace(/[^A-Za-z0-9-]/g, "_")}.pdf`;
  doc.save(fileName);
}

// ==============================================================
// ID Card A4 PDF — same design language as receipt
// ==============================================================
async function drawIDCardBig(doc, member, x, y, w, h) {
  // KELTA blue body with subtle depth
  doc.setFillColor(...KELTA_BLUE);
  doc.roundedRect(x, y, w, h, 5, 5, "F");
  doc.setFillColor(...KELTA_BLUE_SOFT);
  doc.triangle(x + w, y, x + w, y + h * 0.5, x + w * 0.35, y, "F");
  doc.setFillColor(...KELTA_BLUE_DEEP);
  doc.triangle(x, y + h, x, y + h * 0.55, x + w * 0.35, y + h, "F");
  // Tricolor top stripe
  const seg = w / 3;
  doc.setFillColor(...SAFFRON);
  doc.rect(x, y, seg, 2.4, "F");
  doc.setFillColor(255, 255, 255);
  doc.rect(x + seg, y, seg, 2.4, "F");
  doc.setFillColor(...KELTA_GREEN);
  doc.rect(x + 2 * seg, y, seg, 2.4, "F");

  // Association strip
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.text("KERALA LOCAL BODY TECHNICAL ASSISTANTS ASSOCIATION", x + 6, y + 9);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("KELTA MEMBER", x + 6, y + 15);

  // ID No right block
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(220, 232, 250);
  doc.text("ID NUMBER", x + w - 6, y + 9, { align: "right" });
  doc.setFont("courier", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...SAFFRON_SOFT);
  doc.text(member.membership_id || "—", x + w - 6, y + 14.5, { align: "right" });

  // Photo box
  const photoX = x + 6, photoY = y + 22, photoW = 26, photoH = 32;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(photoX - 0.6, photoY - 0.6, photoW + 1.2, photoH + 1.2, 1.5, 1.5, "F");
  const photo = member.photo_url ? await loadDataUrl(member.photo_url) : null;
  if (photo) {
    try { doc.addImage(photo, photoX, photoY, photoW, photoH); } catch { /* ignore */ }
  } else {
    doc.setFillColor(200, 215, 240);
    doc.rect(photoX, photoY, photoW, photoH, "F");
    doc.setTextColor(...KELTA_BLUE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text((member.full_name || "?")[0].toUpperCase(), photoX + photoW / 2, photoY + photoH / 2 + 3, { align: "center" });
  }

  // Details
  const dx = photoX + photoW + 6;
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const name = (member.full_name || "").length > 26 ? (member.full_name || "").slice(0, 25) + "…" : (member.full_name || "");
  doc.text(name, dx, photoY + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(220, 232, 250);
  doc.text(member.designation || "Technical Assistant", dx, photoY + 12);

  const localBody = [member.local_body_name, member.local_body_type].filter(Boolean).join(" ");
  doc.text(localBody || "—", dx, photoY + 18);
  doc.text((member.district || "—") + " District", dx, photoY + 24);

  // QR
  const verifyUrl = `${window.location.origin}/verify/${member.membership_id || "PREVIEW"}`;
  const qr = await genQR(verifyUrl);
  const qrSize = 22;
  const qrX = x + w - qrSize - 6, qrY = y + h - qrSize - 10;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(qrX - 1, qrY - 1, qrSize + 2, qrSize + 2, 1, 1, "F");
  if (qr) doc.addImage(qr, "PNG", qrX, qrY, qrSize, qrSize);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.5);
  doc.setTextColor(220, 232, 250);
  doc.text("SCAN TO VERIFY", qrX + qrSize / 2, qrY + qrSize + 3, { align: "center" });

  // Footer strip inside card
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(220, 232, 250);
  doc.text(`VALID TILL ${fmtDate(member.expiry_date)}`, x + 6, y + h - 3);
  doc.text("kelta.co.in", x + w - 6, y + h - 3, { align: "right" });
}

export async function downloadIDCardPDF(member) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  await drawBrandBackground(doc, { badgeTitle: "MEMBERSHIP\nCERTIFICATE" });

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...KELTA_BLUE);
  doc.text("Official Membership Certificate", 14, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("This document certifies the bearer as an enrolled member of KELTA.", 14, 66);
  drawTricolorAccent(doc, 14, 69, 40, 1.6);

  // Digital card
  const cardX = 22, cardY = 78, cardW = A4.w - 44, cardH = 68;
  await drawIDCardBig(doc, member, cardX, cardY, cardW, cardH);

  // Member particulars card
  const tY = cardY + cardH + 12;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...LINE);
  doc.roundedRect(14, tY, A4.w - 28, 62, 3, 3, "FD");
  drawIconCircle(doc, 22, tY + 8, 4, KELTA_BLUE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...KELTA_BLUE);
  doc.text("MEMBER PARTICULARS", 29, tY + 10);
  doc.setDrawColor(...KELTA_BLUE);
  doc.setLineWidth(0.6);
  doc.line(29, tY + 12, 84, tY + 12);

  const rows = [
    ["Full Name", member.full_name || "—"],
    ["Membership ID", member.membership_id || "—"],
    ["Designation", member.designation || "—"],
    ["Department", member.department || "—"],
    ["Local Body", [member.local_body_name, member.local_body_type].filter(Boolean).join(" ") || "—"],
    ["District", member.district || "—"],
    ["Email", member.email || "—"],
    ["Phone", member.phone || member.mobile || "—"],
    ["Valid Until", fmtDate(member.expiry_date)],
    ["Status", (member.membership_status || "active").replace(/_/g, " ").toUpperCase()],
  ];
  const colW = (A4.w - 30) / 2;
  const rowH = 8.5;
  rows.forEach((r, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const rx = 18 + col * colW;
    const ry = tY + 20 + row * rowH;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.setFontSize(7);
    doc.text(r[0].toUpperCase(), rx, ry);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...KELTA_BLUE);
    doc.setFontSize(8.5);
    const wrapped = doc.splitTextToSize(String(r[1]), colW - 4);
    doc.text(wrapped.slice(0, 2), rx, ry + 4);
  });

  // Signature blocks — President (left) & General Secretary (right)
  const sy = tY + 68;
  const sw = (A4.w - 40) / 2 - 6;
  const { presSign, secSign } = await loadBrandAssets();
  const sigConfig = [
    { label: "President, KELTA", img: presSign, aspect: 1105 / 294, targetH: 16 },
    { label: "General Secretary, KELTA", img: secSign, aspect: 122 / 79, targetH: 14 },
  ];
  sigConfig.forEach(({ label, img, aspect, targetH }, i) => {
    const sx = 20 + i * (sw + 12);
    if (img) {
      const imgH = targetH;
      const imgW = imgH * aspect;
      // center above signature line
      const ix = sx + sw / 2 - imgW / 2;
      const iy = sy + 10 - imgH;
      try { doc.addImage(img, "PNG", ix, iy, imgW, imgH); } catch { /* ignore */ }
    }
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(0.3);
    doc.line(sx, sy + 12, sx + sw, sy + 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...KELTA_BLUE);
    doc.text(label, sx + sw / 2, sy + 17, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text("Authorised Signatory", sx + sw / 2, sy + 21, { align: "center" });
  });

  // Bottom blue band
  const bY = A4.h - 22;
  // White cover to hide the template's baked-in KELTA logo above the band
  doc.setFillColor(255, 255, 255);
  doc.rect(0, bY - 18, A4.w, 18, "F");
  doc.setFillColor(...KELTA_BLUE);
  doc.rect(0, bY, A4.w, 22, "F");
  const { crest } = await loadBrandAssets();
  if (crest) {
    try { doc.addImage(crest, "JPEG", 14, bY + 4, 14, 14); } catch { /* ignore */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("KELTA", 32, bY + 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.text("Kerala Local Body Technical", 32, bY + 14.5);
  doc.text("Assistants Association", 32, bY + 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("CONNECTING PEOPLE   |   EMPOWERING GOVERNANCE", A4.w / 2, bY + 11, { align: "center" });
  doc.text("ENABLING DIGITAL KERALA", A4.w / 2, bY + 15.5, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(`Issued ${fmtDate(new Date())}`, A4.w - 14, bY + 13, { align: "right" });

  const fileName = `KELTA_IDCard_${(member.membership_id || "member").replace(/[^A-Za-z0-9-]/g, "_")}.pdf`;
  doc.save(fileName);
}
